/**
 * Passkey ceremonies in the browser. The server relays the authorization server's
 * `PublicKeyCredential*OptionsJSON` (binary fields in base64url) and expects the credential
 * back as JSON in the same encoding. Only the API ever talks to the authorization server.
 */

type Json = Record<string, unknown>;

/** WebAuthn is available: otherwise every passkey option stays hidden. */
export function webAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential === "function" &&
    typeof navigator.credentials?.get === "function"
  );
}

export function fromBase64Url(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function toBase64Url(buffer: ArrayBuffer | ArrayBufferView): string {
  const bytes =
    buffer instanceof ArrayBuffer
      ? new Uint8Array(buffer)
      : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const str = (value: unknown) => (typeof value === "string" ? value : "");

/** Credential descriptors: their `id` is binary. */
function descriptors(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list)) {
    return undefined;
  }
  return list.map((d: Json) => ({
    ...(d as object),
    type: "public-key",
    id: fromBase64Url(str(d.id)),
  })) as PublicKeyCredentialDescriptor[];
}

export function requestOptions(json: Json): PublicKeyCredentialRequestOptions {
  return {
    ...(json as object),
    challenge: fromBase64Url(str(json.challenge)),
    allowCredentials: descriptors(json.allowCredentials),
  } as PublicKeyCredentialRequestOptions;
}

export function creationOptions(json: Json): PublicKeyCredentialCreationOptions {
  const user = (json.user ?? {}) as Json;
  return {
    ...(json as object),
    challenge: fromBase64Url(str(json.challenge)),
    user: { ...(user as object), id: fromBase64Url(str(user.id)) },
    excludeCredentials: descriptors(json.excludeCredentials),
  } as PublicKeyCredentialCreationOptions;
}

/** The credential as the server expects it (PublicKeyCredential JSON, base64url). */
export function credentialJson(credential: PublicKeyCredential): Json {
  const response = credential.response as AuthenticatorResponse &
    Partial<AuthenticatorAssertionResponse & AuthenticatorAttestationResponse>;
  const out: Json = { clientDataJSON: toBase64Url(response.clientDataJSON) };
  if (response.authenticatorData) {
    out.authenticatorData = toBase64Url(response.authenticatorData);
  }
  if (response.signature) {
    out.signature = toBase64Url(response.signature);
  }
  if (response.userHandle) {
    out.userHandle = toBase64Url(response.userHandle);
  }
  if (response.attestationObject) {
    out.attestationObject = toBase64Url(response.attestationObject);
  }
  if (typeof response.getTransports === "function") {
    out.transports = response.getTransports();
  }
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: out,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
  };
}

/** Signs in with a passkey; rejects when the person cancels or no passkey matches. */
export async function getPasskey(options: Json): Promise<Json> {
  const credential = await navigator.credentials.get({ publicKey: requestOptions(options) });
  if (!credential) {
    throw new Error("no credential");
  }
  return credentialJson(credential as PublicKeyCredential);
}

/** Registers a new passkey with the authenticator. */
export async function createPasskey(options: Json): Promise<Json> {
  const credential = await navigator.credentials.create({ publicKey: creationOptions(options) });
  if (!credential) {
    throw new Error("no credential");
  }
  return credentialJson(credential as PublicKeyCredential);
}
